import { Attachment, IAttachment } from '@app/models/Attachment'
import {
  BrokeragePipeline,
  IBrokeragePipeline,
} from '@app/models/BrokeragePipeline'
import {
  EntityPermissions,
  IEntityPermissions,
} from '@app/models/EntityPermissions'
import { IPotentialProfit, PotentialProfit } from '@app/models/PotentialProfit'
import { ApplicationState } from '@app/store'
import { errorToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest, RequestMethod } from '@app/utils/request'
import { List, Record } from 'immutable'
import { push } from 'connected-react-router'
import { Action, Reducer } from 'redux'
import { fork, put, select, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------

export interface IPipelineState {
  readonly error?: string
  readonly isDeleting: boolean
  readonly isLoading: boolean
  readonly permissions: IEntityPermissions
  readonly pipeline: IBrokeragePipeline
  readonly profit: IPotentialProfit
  readonly showConfirmDelete: boolean
}

const PipelineStateRecord = Record({
  error: undefined,
  isDeleting: false,
  isLoading: false,
  permissions: new EntityPermissions(),
  pipeline: new BrokeragePipeline(),
  profit: undefined,
  showConfirmDelete: false,
})

export class PipelineState extends PipelineStateRecord
  implements IPipelineState {
  public static fromJS(initialState?: Partial<IPipelineState>) {
    if (!initialState) {
      return new PipelineState()
    }

    const { permissions, pipeline, profit, ...rest } = initialState
    return new PipelineState({
      permissions: EntityPermissions.fromJS(permissions),
      pipeline: BrokeragePipeline.fromJS(pipeline),
      profit: PotentialProfit.fromJS(profit),
      ...rest,
    })
  }

  public readonly error?: string
  public readonly isDeleting: boolean
  public readonly isLoading: boolean
  public readonly permissions: EntityPermissions
  public readonly pipeline: IBrokeragePipeline
  public readonly profit: IPotentialProfit
  public readonly showConfirmDelete: boolean

  constructor(props?: Partial<PipelineState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum ActionTypes {
  DeletePipeline = 'PIPELINE::DELETE',
  DeletePipelineFailure = 'PIPELINE::DELETE_FAILURE',
  DeletePipelineRequest = 'PIPELINE::DELETE_REQUEST',
  DeletePipelineSuccess = 'PIPELINE::DELETE_SUCCESS',
  LoadPipeline = 'PIPELINE::LOAD',
  LoadPipelineFailure = 'PIPELINE::LOAD_FAILURE',
  LoadPipelineRequest = 'PIPELINE::LOAD_REQUEST',
  LoadPipelineSuccess = 'PIPELINE::LOAD_SUCCESS',
  SetAttachments = 'PIPELINE::SET_ATTACHMENTS',
  ToggleConfirmDelete = 'PIPELINE::TOGGLE_CONFIRM_DELETE',
  UpdateProbability = 'PIPELINE::UPDATE_PROBABILITY',
  UpdateProbabilitySuccess = 'PIPELINE::UPDATE_PROBABILITY_SUCCESS',
  UpdateProfit = 'PIPELINE::UPDATE_PROFIT',
  UpdateProfitSuccess = 'PIPELINE::UPDATE_PROFIT_SUCCESS',
}

interface IDeletePipelineAction {
  pipelineId: string
  type: ActionTypes.DeletePipeline
}

interface IDeletePipelineFailureAction {
  error: string
  type: ActionTypes.DeletePipelineFailure
}

interface IDeletePipelineRequestAction {
  type: ActionTypes.DeletePipelineRequest
}

interface IDeletePipelineSuccessAction {
  type: ActionTypes.DeletePipelineSuccess
}

interface ILoadPipelineAction {
  pipelineId: string
  type: ActionTypes.LoadPipeline
}

interface ILoadPipelineFailureAction {
  error: string
  type: ActionTypes.LoadPipelineFailure
}

interface ILoadPipelineRequestAction {
  type: ActionTypes.LoadPipelineRequest
}

interface ILoadPipelineSuccessAction {
  permissions: EntityPermissions
  pipeline: BrokeragePipeline
  profit: PotentialProfit
  type: ActionTypes.LoadPipelineSuccess
}

interface ISetAttachmentsAction {
  type: ActionTypes.SetAttachments
  value: List<IAttachment>
}

interface IToggleConfirmDeleteAction {
  show: boolean
  type: ActionTypes.ToggleConfirmDelete
}

interface IUpdateProbabilityAction {
  type: ActionTypes.UpdateProbability
  value: number
}

interface IUpdateProbabilitySuccessAction {
  type: ActionTypes.UpdateProbabilitySuccess
  value: number
}

interface IUpdateProfitAction {
  type: ActionTypes.UpdateProfit
  value: number
}

interface IUpdateProfitSuccessAction {
  type: ActionTypes.UpdateProfitSuccess
  value: number
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | IDeletePipelineFailureAction
  | IDeletePipelineRequestAction
  | IDeletePipelineSuccessAction
  | ILoadPipelineFailureAction
  | ILoadPipelineRequestAction
  | ILoadPipelineSuccessAction
  | ISetAttachmentsAction
  | IToggleConfirmDeleteAction
  | IUpdateProbabilitySuccessAction
  | IUpdateProfitSuccessAction

// -------------------------
// ACTION CREATORS
// -------------------------
function deletePipeline(pipelineId: string): IDeletePipelineAction {
  return {
    pipelineId,
    type: ActionTypes.DeletePipeline,
  }
}

function deletePipelineFailure(error: string): IDeletePipelineFailureAction {
  return {
    error,
    type: ActionTypes.DeletePipelineFailure,
  }
}

function deletePipelineRequest(): IDeletePipelineRequestAction {
  return {
    type: ActionTypes.DeletePipelineRequest,
  }
}

function deletePipelineSuccess(): IDeletePipelineSuccessAction {
  return {
    type: ActionTypes.DeletePipelineSuccess,
  }
}

function loadPipeline(pipelineId: string): ILoadPipelineAction {
  return {
    pipelineId,
    type: ActionTypes.LoadPipeline,
  }
}

function loadPipelineFailure(error: string): ILoadPipelineFailureAction {
  return {
    error,
    type: ActionTypes.LoadPipelineFailure,
  }
}

function loadPipelineRequest(): ILoadPipelineRequestAction {
  return {
    type: ActionTypes.LoadPipelineRequest,
  }
}

function loadPipelineSuccess(
  pipeline: BrokeragePipeline,
  profit: PotentialProfit,
  permissions: EntityPermissions,
): ILoadPipelineSuccessAction {
  return {
    permissions,
    pipeline,
    profit,
    type: ActionTypes.LoadPipelineSuccess,
  }
}

function setAttachments(value: List<IAttachment>): ISetAttachmentsAction {
  return {
    type: ActionTypes.SetAttachments,
    value,
  }
}

function toggleConfirmDelete(show: boolean): IToggleConfirmDeleteAction {
  return {
    show,
    type: ActionTypes.ToggleConfirmDelete,
  }
}

function updateProbability(value: number): IUpdateProbabilityAction {
  return {
    type: ActionTypes.UpdateProbability,
    value,
  }
}

function updateProbabilitySuccess(
  value: number,
): IUpdateProbabilitySuccessAction {
  return {
    type: ActionTypes.UpdateProbabilitySuccess,
    value,
  }
}

function updateProfit(value: number): IUpdateProfitAction {
  return {
    type: ActionTypes.UpdateProfit,
    value,
  }
}

function updateProfitSuccess(value: number): IUpdateProfitSuccessAction {
  return {
    type: ActionTypes.UpdateProfitSuccess,
    value,
  }
}

export const actionCreators = {
  deletePipeline,
  loadPipeline,
  setAttachments,
  toggleConfirmDelete,
  updateProbability,
  updateProbabilitySuccess,
  updateProfit,
  updateProfitSuccess,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<PipelineState> = (
  state: PipelineState = new PipelineState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case ActionTypes.DeletePipelineFailure:
      return state
        .set('error', action.error)
        .set('isDeleting', false) as PipelineState

    case ActionTypes.DeletePipelineRequest:
      return state.set('isDeleting', true) as PipelineState

    case ActionTypes.DeletePipelineSuccess:
      return state
        .set('isDeleting', false)
        .set('pipeline', new BrokeragePipeline()) as PipelineState

    case ActionTypes.LoadPipelineFailure:
      return state
        .set('error', action.error)
        .set('isLoading', false) as PipelineState

    case ActionTypes.LoadPipelineRequest:
      return state.set('isLoading', true) as PipelineState

    case ActionTypes.LoadPipelineSuccess:
      return state
        .set('error', undefined)
        .set('isLoading', false)
        .set('permissions', action.permissions)
        .set('pipeline', action.pipeline)
        .set('profit', action.profit) as PipelineState

    case ActionTypes.SetAttachments:
      return state.setIn(
        ['pipeline', 'listing', 'property', 'attachments'],
        action.value,
      ) as PipelineState

    case ActionTypes.ToggleConfirmDelete:
      return state.set('showConfirmDelete', action.show) as PipelineState

    case ActionTypes.UpdateProbabilitySuccess:
      return state.setIn(
        ['profit', 'completionProbability'],
        action.value,
      ) as PipelineState

    case ActionTypes.UpdateProfitSuccess:
      return state.setIn(
        ['profit', 'estimatedProfit'],
        action.value,
      ) as PipelineState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* deletePipelineSaga(action: IDeletePipelineAction) {
  try {
    yield put(deletePipelineRequest())

    const result = yield makeAuthenticatedRequest(
      `/api/BrokeragePipelines/${action.pipelineId}`,
      { method: RequestMethod.DELETE },
    )

    if (result.success === true) {
      yield put(push('/brokerage/pipeline'))
      yield put(deletePipelineSuccess())
    } else {
      yield put(deletePipelineFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(deletePipelineFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* loadPipelineSaga(action: ILoadPipelineAction) {
  try {
    yield put(loadPipelineRequest())

    const url = `/api/BrokeragePipelines/${action.pipelineId}`

    const result = yield makeAuthenticatedRequest(url)

    if (result.success === true) {
      const baseUrl = `/api/BrokeragePipelines/${action.pipelineId}/PotentialProfit`
      const profitResult = yield makeAuthenticatedRequest(baseUrl)

      if (profitResult.success === true) {
        yield put(
          loadPipelineSuccess(
            BrokeragePipeline.fromJS(result.entity),
            PotentialProfit.fromJS(profitResult.entity),
            EntityPermissions.fromJS(result.permissions),
          ),
        )
      }
    } else {
      yield put(loadPipelineFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(loadPipelineFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* updateProbabilitySaga(action: IUpdateProbabilitySuccessAction) {
  try {
    const currentState: PipelineState = yield select(
      (state: ApplicationState) => state.pipeline,
    )

    const url = `/api/BrokeragePipelines/${currentState.pipeline.id}/PotentialProfit`

    const potential = PotentialProfit.fromJS(currentState.profit).toJS()
    potential.completionProbability = action.value

    const opts = {
      body: JSON.stringify(potential),
      method: RequestMethod.PUT,
    }

    const result = yield makeAuthenticatedRequest(url, opts)

    if (result.success === true) {
      yield put(updateProbabilitySuccess(action.value))
    } else {
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(errorToast('Oops!', err.message))
  }
}

function* updateProfitSaga(action: IUpdateProfitAction) {
  try {
    const currentState: PipelineState = yield select(
      (state: ApplicationState) => state.pipeline,
    )

    const url = `/api/BrokeragePipelines/${currentState.pipeline.id}/PotentialProfit`

    const potential = PotentialProfit.fromJS(currentState.profit).toJS()
    potential.estimatedProfit = action.value

    const opts = {
      body: JSON.stringify(potential),
      method: RequestMethod.PUT,
    }

    const result = yield makeAuthenticatedRequest(url, opts)

    if (result.success === true) {
      yield put(updateProfitSuccess(action.value))
    } else {
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchDeletePipeline() {
  yield takeLatest(ActionTypes.DeletePipeline, deletePipelineSaga)
}

function* watchLoadPipeline() {
  yield takeLatest(ActionTypes.LoadPipeline, loadPipelineSaga)
}

function* watchUpdateProbability() {
  yield takeLatest(ActionTypes.UpdateProbability, updateProbabilitySaga)
}

function* watchUpdateProfit() {
  yield takeLatest(ActionTypes.UpdateProfit, updateProfitSaga)
}

export function* pipelineRootSaga() {
  yield fork(watchDeletePipeline)
  yield fork(watchLoadPipeline)
  yield fork(watchUpdateProbability)
  yield fork(watchUpdateProfit)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = () => (state: ApplicationState) => state.pipeline

export const selectError = () =>
  createSelector(selectModuleState(), (mState) => mState.error)

export const selectIsDeleting = () =>
  createSelector(selectModuleState(), (mState) => mState.isDeleting)

export const selectIsLoading = () =>
  createSelector(selectModuleState(), (mState) => mState.isLoading)

export const selectPermissions = () =>
  createSelector(selectModuleState(), (mState) => mState.permissions)

export const selectPipeline = () =>
  createSelector(selectModuleState(), (mState) => mState.pipeline)

export const selectProfit = () =>
  createSelector(selectModuleState(), (mState) => mState.profit)

export const selectShowConfirmDelete = () =>
  createSelector(selectModuleState(), (mState) => mState.showConfirmDelete)
